import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Plus, Edit3, Trash2, Save, X, Loader2, Search, MapPin, Mail, Phone, IdCard, Award, Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  nome: "", cognome: "", citta: "", provincia: "",
  cf: "", email: "", telefono: "",
  specializzazione: "Consulente Digitale", anno_iscrizione: new Date().getFullYear(),
};

export default function RegistroAdminSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // {id?, ...fields}
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/registro/admin/esse-studi`, { withCredentials: true });
      setItems(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento iscritti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => {
    const q = filter.toLowerCase();
    return !q || [i.nome, i.cognome, i.citta, i.provincia, i.email, i.cf]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const body = { ...editing };
      delete body.id;
      delete body.created_at;
      delete body.updated_at;
      delete body.fonte;
      if (body.anno_iscrizione) body.anno_iscrizione = parseInt(body.anno_iscrizione, 10);
      if (editing.id) {
        await axios.put(`${API}/api/registro/admin/esse-studi/${editing.id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/registro/admin/esse-studi`, body, { withCredentials: true });
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail ? JSON.stringify(e2.response.data.detail) : "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (!confirm(`Eliminare ${nome}?`)) return;
    try {
      await axios.delete(`${API}/api/registro/admin/esse-studi/${id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert("Errore eliminazione");
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold">Registro – Iscritti Esse Studi</h2>
          <p className="text-[13.5px] text-slate-400 mt-1">Gestisci l&apos;elenco degli iscritti Esse Studi (ASN B0196) sincronizzati col Registro pubblico.</p>
        </div>
        <Button
          data-testid="btn-nuovo-iscritto-esse"
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold"
        >
          <Plus className="mr-2 h-4 w-4" /> Nuovo iscritto
        </Button>
      </div>

      {/* Stats banner */}
      <div className="grid sm:grid-cols-3 gap-4">
        <MiniKPI label="Iscritti Esse Studi" value={items.length} icon={Users} color="#f59e0b" />
        <MiniKPI label="Province" value={new Set(items.map(i => i.provincia).filter(Boolean)).size} icon={MapPin} color="#3b82f6" />
        <MiniKPI label="Con email" value={items.filter(i => i.email).length} icon={Mail} color="#22c55e" />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid="search-iscritti-esse"
          placeholder="Cerca per nome, città, CF, email..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-10 h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
        />
      </div>

      {/* Tabella */}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead className="bg-white/5">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="p-4">Nome</th>
                  <th className="p-4">Località</th>
                  <th className="p-4">Codice Fiscale</th>
                  <th className="p-4">Contatti</th>
                  <th className="p-4">Anno</th>
                  <th className="p-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((i) => (
                  <tr key={i.id} data-testid={`esse-row-${i.id}`} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-4">
                      <div className="font-bold">{i.nome} {i.cognome}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{i.specializzazione || "-"}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-semibold">{i.citta || "-"}</div>
                      <div className="text-[11px] text-slate-400">{i.provincia || ""}</div>
                    </td>
                    <td className="p-4 font-mono text-[11.5px] text-slate-300">{i.cf || "-"}</td>
                    <td className="p-4">
                      <div className="text-[12px] text-slate-300 break-all">{i.email || "-"}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{i.telefono || "-"}</div>
                    </td>
                    <td className="p-4">{i.anno_iscrizione || "-"}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          data-testid={`btn-edit-esse-${i.id}`}
                          onClick={() => setEditing(i)}
                          className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center"
                          title="Modifica"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`btn-delete-esse-${i.id}`}
                          onClick={() => handleDelete(i.id, `${i.nome} ${i.cognome}`)}
                          className="h-8 w-8 rounded-lg hover:bg-red-500/15 text-red-400 flex items-center justify-center"
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={6} className="p-8 text-center text-slate-400">Nessun iscritto trovato</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-[13px]">{error}</div>}

      {/* Modal edit/create */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={handleSave} className="bg-[#0a1020] border border-white/10 rounded-2xl p-6 w-full max-w-2xl text-white">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold">{editing.id ? "Modifica iscritto" : "Nuovo iscritto Esse Studi"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Nome *">
                <Input required value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })} data-testid="esse-form-nome" className="bg-white/5 border-white/10 text-white" />
              </Field>
              <Field label="Cognome *">
                <Input required value={editing.cognome} onChange={(e) => setEditing({ ...editing, cognome: e.target.value })} data-testid="esse-form-cognome" className="bg-white/5 border-white/10 text-white" />
              </Field>
              <Field label="Città">
                <Input value={editing.citta || ""} onChange={(e) => setEditing({ ...editing, citta: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </Field>
              <Field label="Provincia (sigla)">
                <Input value={editing.provincia || ""} onChange={(e) => setEditing({ ...editing, provincia: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} className="bg-white/5 border-white/10 text-white uppercase" />
              </Field>
              <Field label="Codice Fiscale">
                <Input value={editing.cf || ""} onChange={(e) => setEditing({ ...editing, cf: e.target.value.toUpperCase() })} maxLength={16} className="bg-white/5 border-white/10 text-white uppercase font-mono" />
              </Field>
              <Field label="Anno iscrizione">
                <Input type="number" value={editing.anno_iscrizione || ""} onChange={(e) => setEditing({ ...editing, anno_iscrizione: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </Field>
              <Field label="Email">
                <Input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </Field>
              <Field label="Telefono">
                <Input value={editing.telefono || ""} onChange={(e) => setEditing({ ...editing, telefono: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </Field>
              <div className="col-span-2">
                <Field label="Specializzazione">
                  <Input value={editing.specializzazione || ""} onChange={(e) => setEditing({ ...editing, specializzazione: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </Field>
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setEditing(null)} className="border-white/10 bg-white/5 hover:bg-white/10 text-white">
                <X className="mr-2 h-4 w-4" /> Annulla
              </Button>
              <Button type="submit" data-testid="btn-salva-esse" disabled={saving} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Salva</>}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function MiniKPI({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center gap-3">
      <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
        <div className="text-2xl font-extrabold">{value}</div>
      </div>
    </div>
  );
}
